import type { Ref } from 'vue'
import { nextTick, ref } from 'vue'
import { $loadTextFromCDN } from '@/helpers'
import { api } from '@/api'
import { CHAPTER_LOCK, CHARGE_TYPE } from '@/constants'
import { useUserStore } from '@/stores'

/**
 * 无限滚动章节数据结构
 */
export interface ScrollChapter {
  id: string
  name: string
  sequenceNo: number
  content: string
  lockStatus: number
  priceAmount: number
  feeType: number
  prevId: string | null
  nextId: string | null
  chapterVersionId: string
  bookChapterFileUrl: string
  bookChapterFreeFileUrl: string
  chapterPrice: number
  loaded: boolean
  loading: boolean
}

/**
 * 无限滚动章节加载 Composable
 * 负责加载章节内容、处理无限滚动、自动解锁等
 */

interface InfiniteChapterOptions {
  contentId: Ref<string>
  initialChapterId: Ref<string>
  book: Ref<any>
  chapterList: Ref<any[]>
  containerRef: Ref<HTMLElement | undefined>
}

export function useInfiniteChapter(options: InfiniteChapterOptions) {
  const {
    contentId,
    initialChapterId,
    book,
    chapterList,
    containerRef,
  } = options

  const userStore = useUserStore()

  // 状态管理
  const chapters = ref<ScrollChapter[]>([])
  const loadingNext = ref(false)
  const loadingPrev = ref(false)
  const hasMoreNext = ref(true)
  const hasMorePrev = ref(true)

  // 预加载配置
  const PRELOAD_COUNT = 2

  /**
   * 判断是否应该自动解锁章节
   */
  function shouldAutoUnlock(chapterItem: any): boolean {
    if (!userStore.$state.autoLock)
      return false

    // 单章付费逻辑
    if (book.value?.chargeType === CHARGE_TYPE.CHAPTER)
      return chapterItem.chapterPrice <= userStore.$state.coinBalance

    // 整本付费逻辑
    if (book.value?.chargeType === CHARGE_TYPE.CONTENT)
      return book.value?.priceAmount <= userStore.$state.coinBalance

    return false
  }

  /**
   * 加载单个章节内容
   */
  async function loadChapterContent(chapterId: string): Promise<ScrollChapter | null> {
    const chapterItem = chapterList.value.find((item: any) => item.id === chapterId)
    if (!chapterItem)
      return null

    const baseChapter: ScrollChapter = {
      id: chapterItem.id,
      name: chapterItem.chapterName,
      sequenceNo: chapterItem.sequenceNo,
      content: '',
      lockStatus: chapterItem.lockStatus,
      priceAmount: book.value?.chargeType === CHARGE_TYPE.CHAPTER ? chapterItem.chapterPrice : book.value?.priceAmount,
      feeType: chapterItem.feeType,
      prevId: chapterItem.preId || null,
      nextId: chapterItem.nextId || null,
      chapterVersionId: chapterItem.chapterVersionId,
      bookChapterFileUrl: chapterItem.bookChapterFileUrl,
      bookChapterFreeFileUrl: chapterItem.bookChapterFreeFileUrl,
      chapterPrice: chapterItem.chapterPrice,
      loaded: false,
      loading: true,
    }

    try {
      // 章节已解锁 - 直接加载完整内容
      if (chapterItem.lockStatus !== CHAPTER_LOCK.LOCK) {
        const content = await $loadTextFromCDN(chapterItem.bookChapterFileUrl)
        return { ...baseChapter, content, loaded: true, loading: false }
      }

      // 章节锁定 - 检查是否应该自动解锁
      if (shouldAutoUnlock(chapterItem)) {
        try {
          const { data } = await api.post<any, PostReadPlayResponse>('/read/play', {
            contentId: contentId.value,
            contentChapterId: chapterId,
            sequenceNo: 1,
          })

          // 更新 chapterList 中的状态
          chapterItem.lockStatus = CHAPTER_LOCK.UNLOCK
          chapterItem.bookChapterFileUrl = data.bookChapterFileUrl

          // 加载完整内容
          const content = await $loadTextFromCDN(data.bookChapterFileUrl)

          // 刷新用户余额
          await userStore.init({ force: true })

          return {
            ...baseChapter,
            content,
            lockStatus: CHAPTER_LOCK.UNLOCK,
            bookChapterFileUrl: data.bookChapterFileUrl,
            loaded: true,
            loading: false,
          }
        }
        catch (error: any) {
          // 自动解锁失败，回退到加载免费部分
          if (error.code === 403 || error.code === 406) {
            const content = await $loadTextFromCDN(chapterItem.bookChapterFreeFileUrl)
            return { ...baseChapter, content, loaded: true, loading: false }
          }
          throw error
        }
      }

      // 不自动解锁 - 只加载免费预览部分
      const content = await $loadTextFromCDN(chapterItem.bookChapterFreeFileUrl)
      return { ...baseChapter, content, loaded: true, loading: false }
    }
    catch (error) {
      console.error('加载章节内容失败:', error)
      return { ...baseChapter, content: '', loaded: false, loading: false }
    }
  }

  /**
   * 加载下一章（可批量加载多章）
   */
  async function loadNextChapters(count: number = 1): Promise<ScrollChapter[]> {
    const result: ScrollChapter[] = []
    const lastChapter = chapters.value[chapters.value.length - 1]

    if (!lastChapter?.nextId) {
      hasMoreNext.value = false
      return result
    }

    // 付费墙：末章为锁定章时，必须解锁后才能继续，禁止向后加载
    if (lastChapter.lockStatus === CHAPTER_LOCK.LOCK) {
      hasMoreNext.value = false
      return result
    }

    let nextId: string | null = lastChapter.nextId

    for (let i = 0; i < count && nextId; i++) {
      // 检查是否已加载
      if (chapters.value.some(c => c.id === nextId))
        break

      const chapter = await loadChapterContent(nextId)
      if (chapter) {
        result.push(chapter)
        // 付费墙：加载到锁定章即停止，将其作为本批最后一章
        if (chapter.lockStatus === CHAPTER_LOCK.LOCK)
          break
        nextId = chapter.nextId
      }
      else {
        break
      }
    }

    const tail = result[result.length - 1]
    if (tail?.lockStatus === CHAPTER_LOCK.LOCK)
      // 末章为锁定章：作为付费墙，停止向后加载
      hasMoreNext.value = false
    else if (result.length > 0)
      hasMoreNext.value = !!tail.nextId
    else if (!nextId)
      hasMoreNext.value = false

    return result
  }

  /**
   * 加载上一章（可批量加载多章）
   */
  async function loadPrevChapters(count: number = 1): Promise<ScrollChapter[]> {
    const result: ScrollChapter[] = []
    const firstChapter = chapters.value[0]

    if (!firstChapter?.prevId) {
      hasMorePrev.value = false
      return result
    }

    let prevId: string | null = firstChapter.prevId

    for (let i = 0; i < count && prevId; i++) {
      // 检查是否已加载
      if (chapters.value.some(c => c.id === prevId))
        break

      const chapter = await loadChapterContent(prevId)
      if (chapter) {
        result.unshift(chapter) // 插入到数组前面
        prevId = chapter.prevId
      }
      else {
        break
      }
    }

    // 修复：只有当最前加载的章节没有 prevId 时才设为 false
    if (result.length > 0)
      hasMorePrev.value = !!result[0].prevId

    else if (!prevId)
      hasMorePrev.value = false

    return result
  }

  /**
   * 向下滚动加载下一章
   */
  async function loadNextChapter(): Promise<void> {
    if (loadingNext.value || !hasMoreNext.value)
      return

    loadingNext.value = true

    try {
      const newChapters = await loadNextChapters(PRELOAD_COUNT)
      chapters.value.push(...newChapters)
    }
    finally {
      loadingNext.value = false
    }
  }

  /**
   * 向上滚动加载上一章（保持滚动位置）
   */
  async function loadPrevChapter(): Promise<void> {
    if (loadingPrev.value || !hasMorePrev.value)
      return

    loadingPrev.value = true

    try {
      // 加载上一章
      const newChapters = await loadPrevChapters(PRELOAD_COUNT)

      // 在 unshift 紧前面同步捕获滚动状态（避免异步加载期间 iOS 惯性滚动导致值失效）
      const firstChapterEl = containerRef.value?.querySelector('[data-chapter-id]') as HTMLElement
      const scrollTopBefore = containerRef.value!.scrollTop
      const offsetTopBefore = firstChapterEl?.offsetTop || 0

      chapters.value.unshift(...newChapters)

      // 使用 double rAF 确保 iOS Safari 完成布局后再恢复滚动位置
      // 注意：不能直接将 `resolve` 作为 rAF 的回调，TS 会报类型不兼容
      await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))
      const offsetTopAfter = firstChapterEl?.offsetTop || 0
      containerRef.value!.scrollTop = scrollTopBefore + (offsetTopAfter - offsetTopBefore)
    }
    finally {
      loadingPrev.value = false
    }
  }

  /**
   * 手动解锁章节
   */
  async function unlockChapter(chapterId: string): Promise<boolean> {
    const chapter = chapters.value.find(c => c.id === chapterId)
    if (!chapter)
      return false

    try {
      // 调用解锁 API
      const { data } = await api.post<any, PostReadPlayResponse>('/read/play', {
        contentId: contentId.value,
        contentChapterId: chapterId,
        sequenceNo: 1,
      })

      // 更新 chapterList 中的状态
      const chapterItem = chapterList.value.find((item: any) => item.id === chapterId)
      if (chapterItem) {
        chapterItem.lockStatus = CHAPTER_LOCK.UNLOCK
        chapterItem.bookChapterFileUrl = data.bookChapterFileUrl
      }

      // 加载完整内容
      const content = await $loadTextFromCDN(data.bookChapterFileUrl)

      // 更新章节数据（原地替换）
      chapter.content = content
      chapter.lockStatus = CHAPTER_LOCK.UNLOCK
      chapter.bookChapterFileUrl = data.bookChapterFileUrl

      // 付费墙解锁：若该章原是阻断加载的末章，恢复向后加载
      const isTail = chapters.value[chapters.value.length - 1]?.id === chapter.id
      if (isTail && chapter.nextId)
        hasMoreNext.value = true

      // 刷新用户余额
      await userStore.init({ force: true })

      // 滚动到该章节顶部
      await nextTick()
      const chapterEl = containerRef.value?.querySelector(`[data-chapter-id="${chapterId}"]`) as HTMLElement
      if (chapterEl)
        chapterEl.scrollIntoView({ behavior: 'smooth', block: 'start' })

      return true
    }
    catch (error: any) {
      console.error('解锁章节失败:', error)
      return false
    }
  }

  /**
   * 滚动到指定章节
   */
  async function scrollToChapter(chapterId: string): Promise<void> {
    await nextTick()
    const chapterEl = containerRef.value?.querySelector(`[data-chapter-id="${chapterId}"]`) as HTMLElement
    if (chapterEl && containerRef.value)
      containerRef.value.scrollTop = chapterEl.offsetTop
  }

  /**
   * 初始化：加载当前章节 + 上下各2章
   * 注意：不在此处滚动，由调用方在 DOM 渲染后调用 scrollToChapter
   */
  async function init(): Promise<void> {
    chapters.value = []
    hasMoreNext.value = true
    hasMorePrev.value = true

    // 1. 加载当前章节
    const currentChapter = await loadChapterContent(initialChapterId.value)
    if (!currentChapter)
      return

    chapters.value = [currentChapter]

    // 2. 预加载前2章
    if (currentChapter.prevId) {
      const prevChapters = await loadPrevChapters(PRELOAD_COUNT)
      chapters.value.unshift(...prevChapters)
      // 修复：根据实际加载的第一章是否还有 prevId 来设置
      if (prevChapters.length > 0)
        hasMorePrev.value = !!prevChapters[0].prevId
    }
    else {
      hasMorePrev.value = false
    }

    // 3. 预加载后2章（loadNextChapters 内部已处理付费墙与 hasMoreNext）
    if (currentChapter.nextId) {
      const nextChapters = await loadNextChapters(PRELOAD_COUNT)
      chapters.value.push(...nextChapters)
    }
    else {
      hasMoreNext.value = false
    }

    // 注意：滚动操作由调用方在 loading=false、DOM 渲染后执行
  }

  return {
    chapters,
    loadingNext,
    loadingPrev,
    hasMoreNext,
    hasMorePrev,
    loadNextChapter,
    loadPrevChapter,
    unlockChapter,
    scrollToChapter,
    init,
  }
}
